/**
 * Papers written entirely in another script: Bahasa Arab is set in Arabic and Pendidikan Islam in Jawi (the real
 * KPM papers for these subjects are not in Rumi). The renderer translates its fixed text (headings, instructions,
 * labels) through `localise`; the AI writes the questions in the same script and `scriptProblem` rejects papers that
 * slipped back into Malay.
 */
export type PaperScript = "ar" | "jawi";

/** Script a subject's whole paper is written in (primary school), else null. */
export function paperScript(subject: string, level: "rendah" | "menengah" = "rendah"): PaperScript | null {
  if (level !== "rendah") return null;
  return subject === "bahasa_arab" ? "ar" : subject === "pendidikan_islam" ? "jawi" : null;
}

const INDIC = "٠١٢٣٤٥٦٧٨٩";
export const toIndic = (s: string | number) => String(s).replace(/\d/g, (d) => INDIC[Number(d)]!);
/** Option letters A–D and part labels a–f in the paper's script. */
export const OPTION_LETTERS = ["أ", "ب", "ج", "د"] as const;
const PART_LETTERS: Record<string, string> = { a: "أ", b: "ب", c: "ج", d: "د", e: "هـ", f: "و", g: "ز", h: "ح" };
export const scriptLetter = (l: string) => PART_LETTERS[l.toLowerCase()] ?? l;
export const optionLetter = (k: number) => OPTION_LETTERS[k] ?? "ABCD"[k]!;

const ORD = ["", "الأولى", "الثانية", "الثالثة", "الرابعة", "الخامسة", "السادسة"];

type Pair = { ar: string; jawi: string };
const FIXED: Record<string, Pair> = {
  "SULIT": { ar: "سري", jawi: "سوليت" },
  "SET SOALAN LATIHAN": { ar: "مجموعة أسئلة تدريبية", jawi: "سيت سوالن لاتيهن" },
  "JANGAN BUKA KERTAS SOALAN SEHINGGA DIBERITAHU": { ar: "لا تفتح ورقة الأسئلة حتى يُؤذَن لك", jawi: "جاڠن بوک کرتاس سوالن سهيڠݢ دبريتاهو" },
  "JANGAN BUKA KERTAS UJIAN INI SEHINGGA DIBERITAHU": { ar: "لا تفتح ورقة الاختبار هذه حتى يُؤذَن لك", jawi: "جاڠن بوک کرتاس اوجين اين سهيڠݢ دبريتاهو" },
  "Nama :": { ar: "الاسم :", jawi: "نام :" },
  "Kelas :": { ar: "الصف :", jawi: "کلس :" },
  "Nama": { ar: "الاسم", jawi: "نام" },
  "Kelas": { ar: "الصف", jawi: "کلس" },
  "Sekolah": { ar: "المدرسة", jawi: "سکوله" },
  "ARAHAN:": { ar: "تعليمات:", jawi: "ارهن:" },
  "Untuk Kegunaan Pemeriksa": { ar: "للمصحِّح فقط", jawi: "اونتوق کݢوناءن ڤمريقسا" },
  "Untuk": { ar: "خاص", jawi: "اونتوق" },
  "Kegunaan": { ar: "بالمصحِّح", jawi: "کݢوناءن" },
  "Pemeriksa": { ar: "فقط", jawi: "ڤمريقسا" },
  "Nama pemeriksa:": { ar: "اسم المصحِّح:", jawi: "نام ڤمريقسا:" },
  "Bahagian": { ar: "القسم", jawi: "بهاݢين" },
  "Nombor soalan": { ar: "رقم السؤال", jawi: "نومبور سوالن" },
  "Markah penuh": { ar: "الدرجة الكاملة", jawi: "مرقه ڤنوه" },
  "Markah diperoleh": { ar: "الدرجة المحصَّلة", jawi: "مرقه دڤروليه" },
  "Jumlah": { ar: "المجموع", jawi: "جملة" },
  "[Sila lihat sebelah": { ar: "[انظر الصفحة التالية", jawi: "[سيلا ليهت سبله" },
  "KERTAS SOALAN TAMAT": { ar: "انتهت الأسئلة", jawi: "کرتاس سوالن تمت" },
  "SKEMA JAWAPAN": { ar: "مفتاح الإجابات", jawi: "سکيما جواڤن" },
  "Pernyataan": { ar: "العبارة", jawi: "ڤرنياتاءن" },
  "Jawab semua soalan.": { ar: "أجب عن جميع الأسئلة.", jawi: "جواب سموا سوالن." },
  "Jawab satu soalan sahaja.": { ar: "أجب عن سؤال واحد فقط.", jawi: "جواب ساتو سوالن سهاج." },
  "Jawab semua soalan yang diberikan.": { ar: "أجب عن جميع الأسئلة المعطاة.", jawi: "جواب سموا سوالن يڠ دبريکن." },
  "Bahagian ini mengandungi dua soalan. Jawab satu soalan sahaja.": { ar: "يحتوي هذا القسم على سؤالين. أجب عن سؤال واحد فقط.", jawi: "بهاݢين اين مڠاندوڠي دوا سوالن. جواب ساتو سوالن سهاج." },
  "Latihan ikut DSKP": { ar: "تدريب وفق المنهج", jawi: "لاتيهن ايکوت DSKP" },
  "LATIHAN - bukan kertas rasmi KPM": { ar: "تدريب - ليست ورقة رسمية", jawi: "لاتيهن - بوکن کرتاس رسمي KPM" },
  "Jawapan dijana oleh AI dan disemak secara automatik. Guru disarankan menyemak sebelum digunakan.": { ar: "أُعدَّت الإجابات بالذكاء الاصطناعي وروجعت آليًا. يُنصح المعلم بمراجعتها قبل الاستخدام.", jawi: "جواڤن دجان اوليه AI دان دسمق سچارا اوتوماتيک. ݢورو دسارنکن مڽمق سبلوم دݢوناکن." },
  "(betul)": { ar: "(صح)", jawi: "(بتول)" },
  "(salah)": { ar: "(خطأ)", jawi: "(سله)" },
  "PENDIDIKAN ISLAM": { ar: "التربية الإسلامية", jawi: "ڤنديديقن إسلام" },
  "BAHASA ARAB": { ar: "اللغة العربية", jawi: "بهاس عرب" },
  "BAHAGIAN": { ar: "القسم", jawi: "بهاݢين" },
  "SEKSYEN": { ar: "القسم", jawi: "بهاݢين" },
};
const FIXED_CI = new Map(Object.entries(FIXED).map(([k, v]) => [k.toLowerCase(), v]));

const secId = (id: string) => ({ A: "أ", B: "ب", C: "ج", D: "د" } as Record<string, string>)[id] ?? id;

/** Translate one fixed piece of paper text; anything unrecognised (question content) is returned unchanged. */
export function localise(script: PaperScript | null | undefined, s: string): string {
  if (!script || !s) return s;
  const hit = FIXED_CI.get(s.trim().toLowerCase());
  if (hit) return hit[script];
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^BAHAGIAN ([A-D])$/i)) || (m = s.match(/^Bahagian ([A-D])$/))) return `${FIXED["BAHAGIAN"]![script]} ${secId(m[1]!.toUpperCase())}`;
  if ((m = s.match(/^\[(\d+) markah\]$/))) return `[${toIndic(m[1]!)} ${script === "ar" ? "درجة" : "مرقه"}]`;
  if ((m = s.match(/^Soalan (\d+)$/))) return `${script === "ar" ? "السؤال" : "سوالن"} ${toIndic(m[1]!)}`;
  if ((m = s.match(/^Rajah (\d+)$/))) return `${script === "ar" ? "الشكل" : "راجه"} ${toIndic(m[1]!)}`;
  if ((m = s.match(/^Jadual (\d+)$/))) return `${script === "ar" ? "الجدول" : "جدول"} ${toIndic(m[1]!)}`;
  if ((m = s.match(/^(?:Tahun) ([1-6])$/i))) return script === "ar" ? `السنة ${ORD[Number(m[1])]}` : `تاهون ${toIndic(m[1]!)}`;
  if ((m = s.match(/^Kertas ([12])$/i))) return `${script === "ar" ? "الورقة" : "کرتاس"} ${toIndic(m[1]!)}`;
  if ((m = s.match(/^(\d+)(½|¼|¾)? jam$/))) return durationScript(script, Number(m[1]) * 60 + (m[2] === "½" ? 30 : m[2] === "¼" ? 15 : m[2] === "¾" ? 45 : 0));
  return s;
}

/** Duration in the paper's script: "ساعة ونصف", "١ جم ٣٠ منيت". */
export function durationScript(script: PaperScript, min: number): string {
  const h = Math.floor(min / 60), m = min % 60;
  if (script === "ar") {
    if (!h) return `${toIndic(m)} دقيقة`;
    const hs = h === 1 ? "ساعة" : h === 2 ? "ساعتان" : `${toIndic(h)} ساعات`;
    return m === 0 ? hs : m === 30 ? `${hs} ونصف` : m === 15 ? `${hs} وربع` : `${hs} و${toIndic(m)} دقيقة`;
  }
  return [h ? `${toIndic(h)} جم` : "", m ? `${toIndic(m)} منيت` : ""].filter(Boolean).join(" ");
}

/** Cover instructions (Tahun 1–3 style), in the paper's script. */
export function coverInstructions(script: PaperScript, o: { ids: string[]; objId?: string; chooseId?: string }): string[] {
  const ar = script === "ar";
  const secs = o.ids.map((i) => `${ar ? "القسم" : "بهاݢين"} ${secId(i)}`);
  const list = secs.length > 1 ? `${secs.slice(0, -1).join("، ")} ${ar ? "و" : "دان"} ${secs[secs.length - 1]}` : secs[0]!;
  const n = toIndic(o.ids.length);
  return ar
    ? [
        `تحتوي ورقة الأسئلة هذه على ${n} أقسام: ${list}.`,
        "أجب عن جميع الأسئلة.",
        ...(o.objId ? [`كل سؤال في القسم ${secId(o.objId)} يليه أربعة اختيارات هي أ، ب، ج، د. اختر إجابة واحدة فقط لكل سؤال، ثم ضع دائرة حول الإجابة.`] : []),
        ...(o.chooseId ? [`في القسم ${secId(o.chooseId)} أجب عن السؤال ١ أو السؤال ٢.`] : []),
        o.objId ? "إذا أردت تغيير إجابتك فامسح العلامة ثم ضع دائرة حول الإجابة الجديدة." : "اكتب إجاباتك في المكان المخصص في ورقة الأسئلة.",
      ]
    : [
        `کرتاس سوالن اين مڠاندوڠي ${n} بهاݢين: ${list}.`,
        "جواب سموا سوالن.",
        ...(o.objId ? [`تياڤ٢ سوالن دالم بهاݢين ${secId(o.objId)} دايكوتي اوليه ايمڤت ڤيليهن جواڤن، ايايت أ، ب، ج دان د. باݢي ستياڤ سوالن، ڤيليه ساتو جواڤن سهاج. بولتکن جواڤن کامو.`] : []),
        ...(o.chooseId ? [`باݢي بهاݢين ${secId(o.chooseId)}، کامو بوليه مميليه سام ادا منجواب سوالن ١ اتاو سوالن ٢.`] : []),
        o.objId ? "جيک کامو هندق منوکر جواڤن، ڤادمکن تندا يڠ تله دبوات. کمودين بولتکن جواڤن يڠ بهارو." : "جواڤن کامو هندقله دتوليس ڤادا روڠ يڠ دسدياکن دالم کرتاس سوالن اين.",
      ];
}

/** Cover instructions (Tahun 4–6 style). */
export function coverInstructionsT2(script: PaperScript, o: { ids: string[]; chooseId?: string }): string[] {
  const ar = script === "ar";
  const secs = o.ids.map((i) => `${ar ? "القسم" : "بهاݢين"} ${secId(i)}`);
  const list = secs.length > 1 ? `${secs.slice(0, -1).join("، ")} ${ar ? "و" : "دان"} ${secs[secs.length - 1]}` : secs[0]!;
  const n = toIndic(o.ids.length);
  return ar
    ? [
        `تحتوي ورقة الاختبار هذه على ${n} أقسام وهي ${list}.`,
        o.chooseId ? `أجب عن جميع الأسئلة في الأقسام الأخرى. في القسم ${secId(o.chooseId)} أجب عن سؤال واحد فقط.` : "أجب عن جميع الأسئلة المعطاة.",
        `اكتب إجاباتك في المكان المخصص لـ ${list}.`,
      ]
    : [
        `کرتاس اوجين اين مڠاندوڠي ${n} بهاݢين ايايت ${list}.`,
        o.chooseId ? `جواب سموا سوالن دالم بهاݢين لاين. باݢي بهاݢين ${secId(o.chooseId)}، جواب ساتو سوالن سهاج.` : "جواب سموا سوالن يڠ دبريکن.",
        `جواب ڤادا روڠن يڠ دسدياکن اونتوق ${list}.`,
      ];
}

export const pagesLine = (script: PaperScript, n: number) =>
  script === "ar" ? `تحتوي ورقة الأسئلة هذه على ${toIndic(n)} صفحات مطبوعة.` : `کرتاس سوالن اين مڠاندوڠي ${toIndic(n)} حلامن برچتق.`;

// ── Validation ─────────────────────────────────────────────────────────────────
const AR_LETTER = /[ء-يٮ-ۓݐ-ݿ]/g;
const LATIN_LETTER = /[A-Za-z]/g;

/** True when `text` is written in the Arabic script (a short Latin remainder — numbers, DSKP, 'x' — is tolerated). */
export function inScript(text: string): boolean {
  const ar = (text.match(AR_LETTER) ?? []).length, lat = (text.match(LATIN_LETTER) ?? []).length;
  return ar > 0 && lat <= Math.max(2, ar * 0.15);
}

/** Why a stem breaks the paper's script rule, or null. */
export function scriptProblem(script: PaperScript | null, text: string | undefined): string | null {
  if (!script || !text || !text.trim()) return null;
  if (!/[A-Za-z\u0621-\u064A\u066E-\u06D3\u0750-\u077F]/.test(text)) return null; // pure numbers / symbols
  const roman=text.replace(/\([a-z]\)/gi, "").replace(/\b(?:DSKP|KSSR|KPM|AI|SAW|SWT|RM|SP|SK|cm|mm|km|kg|ml)\b/g, "");
  const mixedWord=/[A-Za-z]{2,}/.test(roman);
  return inScript(roman) && !mixedWord ? null : script === "ar" ? "mesti ditulis sepenuhnya dalam bahasa Arab (huruf Arab)" : "mesti ditulis sepenuhnya dalam tulisan Jawi (huruf Arab/Jawi), bukan Rumi";
}

